<template>
  <!-- Vue's Teleport moves only the DOM, so the drawer still injects Vuetify's
       layout and positions itself as an app drawer, while its markup lands
       outside the page card whose stacking context would seal it in.
       `temporary` keeps it out of the layout's space accounting (Vue 2's
       `app temporary`); `persistent` and the rest of the call site's
       attributes fall through to it. A panel opened from inside a dialog stays
       in that dialog's overlay, as the in-place v2 drawer did, or it would land
       under it; hosts without a `v-app` (component specs) have no target, so
       the drawer stays in place there. -->
  <Teleport :to="teleportTarget" :disabled="!teleportTarget" defer>
    <v-navigation-drawer
      v-model="showDialog"
      v-bind="$attrs"
      class="sc-side-panel"
      :width="width"
      location="end"
      :order="stackOrder"
      temporary
      touchless
      disable-route-watcher
    >
      <div class="sc-side-panel__content" :style="contentStyle">
        <!-- A panel is its own surface: `root` drops the defaults of the page
             that opened it (the property record's outlined cards), so a panel
             looks the same wherever it is opened. -->
        <v-defaults-provider root>
          <slot />
        </v-defaults-provider>
      </div>
    </v-navigation-drawer>
  </Teleport>
</template>

<script lang="ts">
import { defineComponent, type StyleValue } from 'vue';

import { DialogMixin } from '@/bundles/Dialogs/mixins/DialogMixin';
import type { Nullable } from '@/utils/types';

interface ICommonSidePanelData {
  hostFontSize: Nullable<string>;
  teleportTarget: Nullable<Element>;
  stackOrder: number;
  holdsScrollLock: boolean;
}

// Vuetify stacks layout items by `order` (lower on top), then by registration:
// a panel mounted later sits under one mounted earlier. So a panel opened from
// another one (Create a New Availability over Create New Comparable) or beside
// it (the requirement form over Add requirement) opened behind it. Each panel
// takes the next, lower order when it opens: the latest opened is on top.
let openSequence = 0;

// A drawer does not block the page scroll the way a dialog does, so the page
// behind an open panel scrolled with the wheel, and a scroll inside the panel
// ran on into the page once it hit the end. Panels open on the page share one
// lock on <html>, taken the way Vuetify's own dialogs take it (its
// `v-overlay-scroll-blocked` class pins the page at its scroll offset). A lock a
// dialog already holds is left to that dialog.
let scrollLockHolders = 0;
let ownsScrollLock = false;

function lockPageScroll (): void {
  scrollLockHolders += 1;

  const html = document.documentElement;

  if (scrollLockHolders > 1 || html.classList.contains('v-overlay-scroll-blocked')) return;

  ownsScrollLock = true;
  html.style.setProperty('--v-body-scroll-x', `${-html.scrollLeft}px`);
  html.style.setProperty('--v-body-scroll-y', `${-html.scrollTop}px`);
  html.classList.add('v-overlay-scroll-blocked');
}

function unlockPageScroll (): void {
  scrollLockHolders -= 1;

  if (scrollLockHolders > 0 || !ownsScrollLock) return;

  ownsScrollLock = false;

  const html = document.documentElement;
  const x = -parseFloat(html.style.getPropertyValue('--v-body-scroll-x'));
  const y = -parseFloat(html.style.getPropertyValue('--v-body-scroll-y'));
  const { scrollBehavior } = html.style;

  html.style.removeProperty('--v-body-scroll-x');
  html.style.removeProperty('--v-body-scroll-y');
  html.classList.remove('v-overlay-scroll-blocked');
  html.style.scrollBehavior = 'auto';
  html.scrollLeft = x;
  html.scrollTop = y;
  html.style.scrollBehavior = scrollBehavior;
}

// Right-docked modal form panel: the Vue 2 `v-navigation-drawer app right
// temporary` written in place. Vuetify 3's `.v-card` sets `z-index: 0`, so a
// drawer rendered inside a page card is sealed into that card's stacking
// context and no z-index can lift it over the app's overlays; the drawer is
// teleported to the application wrap instead. The styling lives in
// `sass/base/_styles.scss` under `.sc-side-panel`.
export default defineComponent({
  name: 'CommonSidePanel',
  mixins: [DialogMixin],
  // The root is a Teleport, which cannot carry attributes; they go to the drawer.
  inheritAttrs: false,

  props: {
    width: {
      type: [Number, String],
      default: 600,
    },
  },

  data: (): ICommonSidePanelData => ({
    hostFontSize: null,
    teleportTarget: null,
    stackOrder: 0,
    holdsScrollLock: false,
  }),

  computed: {
    contentStyle (): StyleValue | undefined {
      return this.hostFontSize ? { fontSize: this.hostFontSize } : undefined;
    },
  },

  watch: {
    modelValue (open: boolean): void {
      this.holdPageScroll(open);

      if (!open) return;
      this.bringToFront();
      this.readHostFontSize();
    },
  },

  // `created`, not `mounted`: a host panel is created before the panels inside
  // it (mounted runs child-first), so an already-open nested one still ends up
  // above its host.
  created (): void {
    if (this.modelValue) this.bringToFront();
  },

  mounted (): void {
    // Resolved after mount, since the wrap is the app root's own DOM and is
    // not there yet while the panel is being created inside it.
    this.teleportTarget = (this.$el as Nullable<Node>)?.parentElement?.closest('.v-overlay__content') ??
      document.querySelector('.v-application__wrap');
    this.readHostFontSize();
    this.holdPageScroll(this.modelValue);
  },

  beforeUnmount (): void {
    this.holdPageScroll(false);
  },

  methods: {
    bringToFront (): void {
      openSequence += 1;
      this.stackOrder = -openSequence;
    },
    // Only a panel teleported onto the page holds the lock: one inside a
    // dialog sits in an overlay that already blocks the page, and one without
    // an application to teleport to (a component spec) has no page to lock.
    holdPageScroll (hold: boolean): void {
      const onPage = !!this.teleportTarget?.classList.contains('v-application__wrap');

      if (hold && onPage && !this.holdsScrollLock) {
        this.holdsScrollLock = true;
        lockPageScroll();
      } else if (!hold && this.holdsScrollLock) {
        this.holdsScrollLock = false;
        unlockPageScroll();
      }
    },
    // The v2 drawer was rendered in place, so it inherited its host's font
    // size: a form opened from inside a card text ran at that text's 14px, one
    // opened from a toolbar at 16px. The teleported panel starts from <body>
    // (16px) instead, so the property spec forms (Site Details, Rail, Parking,
    // ...) would grow from 14px to 16px. Read the host's computed size and hand
    // it to the panel content; the teleport placeholder is the component's
    // root node, and its parent is the element the panel was written into.
    readHostFontSize (): void {
      const host = (this.$el as Nullable<Node>)?.parentElement;

      if (!host) return;

      const { fontSize } = getComputedStyle(host);

      this.hostFontSize = fontSize || null;
    },
  },
})
</script>
